/*
"Captures par mois" hand-rolled SVG bar chart.

Why hand-roll instead of pulling a chart library:
- The data scope is tiny — at most 24 months of buckets, each one
  integer-valued. A library would dwarf the entry chunk for what
  amounts to 12-24 rectangles.
- Total dependency surface stays at zero (already enforced for the
  PWA bundle).
- We get full control over the season-coloured bars + value labels,
  which is the visual hook that lifts this above a generic chart.

Aggregation contract (mirrored on what the Profile passes):
- Captures are bucketed into YearMonth strings ("2026-05"), keyed
  in chronological order.
- Empty months between the first and last capture month are filled
  with zero-count entries so the bar timeline reads as a continuous
  axis (no jumps from May → August). This is the same trick the
  Android Phase 12 chart uses.
*/

import type { Capture } from '../../data/types'
import { seasonFromTimestamp } from '../../data/season'

const kMaxMonths: number = 18  // cap the visible window so the chart doesn't widen forever
const kChartHeight: number = 132  // px, drawn into the viewBox 1:1
const kChartPadding = { top: 16, right: 8, bottom: 28, left: 8 }  // px reserved for axis labels + headroom for value text
const kBarMinHeightPx: number = 2  // every non-zero bar is at least this tall so 1-capture months still register visually

// Brand-aligned tones per season ordinal
// (WINTER=0, SPRING=1, SUMMER=2, AUTUMN=3) — intentional contrast
// so adjacent month bars in different seasons are visually distinct.
const kSeasonColors: readonly string[] = [
	'#7da4b8',  // WINTER — muted blue
	'#6fae5a',  // SPRING — feuilleClaire
	'#c8a24a',  // SUMMER — or
	'#d97a3a',  // AUTUMN — remarquableOrange
]

interface MonthBucket
{
	yearMonth: string  // "2026-05"
	captures: number   // bar height proxy
	newSpecies: number // visible as a small badge on top of the bar
	seasonIndex: number  // 0..3 — drives bar colour
	monthLabel: string   // "M" or "05" for axis tick
}

/*
Render the chart shell. Returns the SVG markup as a string so the
caller can drop it inside a card. Pure function of the captures
array — no event listeners; the chart is informational, not
interactive (a future evolution can wire tooltips / drill-down).
*/
export function renderMonthlyChart(inCaptures: Capture[]): string
{
	if (inCaptures.length === 0)
	{
		return `<div class="monthly-chart-empty">Aucune capture pour le moment.</div>`
	}

	const vBuckets = bucketByMonth(inCaptures)
	if (vBuckets.length === 0)
	{
		return `<div class="monthly-chart-empty">Aucune capture sur les ${kMaxMonths} derniers mois.</div>`
	}

	const vMaxCount = Math.max(1, ...vBuckets.map((inB) => inB.captures))
	const vBarSlot = 100 / vBuckets.length  // % width per month slot in the SVG viewBox-x

	const vSvgHeight = kChartHeight
	const vAvailableH = vSvgHeight - kChartPadding.top - kChartPadding.bottom

	const vBarsMarkup = vBuckets.map((inBucket, inIdx) => {
		const vSlotXPercent = inIdx * vBarSlot
		const vBarWidthPercent = vBarSlot * 0.66  // 66 % of the slot for the bar, rest is gap
		const vBarXPercent = vSlotXPercent + (vBarSlot - vBarWidthPercent) / 2
		const vRatio = inBucket.captures / vMaxCount
		const vBarHeight = inBucket.captures > 0 ? Math.max(kBarMinHeightPx, vRatio * vAvailableH) : 0
		const vBarY = kChartPadding.top + (vAvailableH - vBarHeight)
		const vColor = kSeasonColors[inBucket.seasonIndex]
		const vLabelY = vSvgHeight - kChartPadding.bottom + 14
		const vCountY = vBarY - 4

		// Show the count above tall bars; suppress on tiny bars to keep
		// the chart from looking noisy.
		const vCountLabel = (inBucket.captures > 0 && vRatio > 0.18)
			? `<text x="${(vBarXPercent + vBarWidthPercent / 2).toFixed(2)}%" y="${vCountY}" class="monthly-chart-count" text-anchor="middle">${inBucket.captures}</text>`
			: ''

		// "+N" species badge on bars where the user discovered new ones
		// — the most informative cell on the chart for an Arboretum
		// builder, communicating *progress* on top of *activity*.
		const vNewBadge = inBucket.newSpecies > 0
			? `<circle cx="${(vBarXPercent + vBarWidthPercent).toFixed(2)}%" cy="${(vBarY + 5).toFixed(0)}" r="5" class="monthly-chart-new-badge" />
			   <text x="${(vBarXPercent + vBarWidthPercent).toFixed(2)}%" y="${(vBarY + 8).toFixed(0)}" class="monthly-chart-new-text" text-anchor="middle">+${inBucket.newSpecies}</text>`
			: ''

		return `
			<g>
				<rect x="${vBarXPercent.toFixed(2)}%" y="${vBarY.toFixed(2)}" width="${vBarWidthPercent.toFixed(2)}%" height="${vBarHeight.toFixed(2)}" rx="3" fill="${vColor}" class="monthly-chart-bar"/>
				${vCountLabel}
				<text x="${(vBarXPercent + vBarWidthPercent / 2).toFixed(2)}%" y="${vLabelY}" class="monthly-chart-axis" text-anchor="middle">${inBucket.monthLabel}</text>
				${vNewBadge}
			</g>
		`
	}).join('')

	const vTotal = vBuckets.reduce((inAcc, inB) => inAcc + inB.captures, 0)
	const vTotalNew = vBuckets.reduce((inAcc, inB) => inAcc + inB.newSpecies, 0)

	return `
		<div class="monthly-chart">
			<svg viewBox="0 0 100 ${vSvgHeight}" preserveAspectRatio="none" class="monthly-chart-svg" role="img" aria-label="Captures par mois">
				${vBarsMarkup}
			</svg>
			<div class="monthly-chart-legend">
				<span class="monthly-chart-legend-cell"><span class="monthly-chart-swatch" style="background:${kSeasonColors[0]}"></span>Hiver</span>
				<span class="monthly-chart-legend-cell"><span class="monthly-chart-swatch" style="background:${kSeasonColors[1]}"></span>Printemps</span>
				<span class="monthly-chart-legend-cell"><span class="monthly-chart-swatch" style="background:${kSeasonColors[2]}"></span>Été</span>
				<span class="monthly-chart-legend-cell"><span class="monthly-chart-swatch" style="background:${kSeasonColors[3]}"></span>Automne</span>
			</div>
			<div class="monthly-chart-summary">
				${vTotal} capture${vTotal > 1 ? 's' : ''}${vTotalNew > 0 ? ` · ${vTotalNew} nouvelle${vTotalNew > 1 ? 's' : ''} espèce${vTotalNew > 1 ? 's' : ''}` : ''} sur cette fenêtre.
			</div>
		</div>
	`
}

/*
Aggregate captures into ordered MonthBucket entries. Empty months
between the first and last bucket are filled with zero-count entries
so the timeline reads continuously. The window is right-aligned
(latest month always present) and capped at kMaxMonths.
*/
function bucketByMonth(inCaptures: Capture[]): MonthBucket[]
{
	if (inCaptures.length === 0) return []
	// Pre-compute the discovery date of each species across the user's
	// timeline — used to count "new species first appeared this month"
	// per bucket. Done once instead of per-bar.
	const vSpeciesFirstSeen = new Map<number, number>()  // speciesIndex -> earliest timestamp
	for (const vCap of inCaptures)
	{
		const vPrev = vSpeciesFirstSeen.get(vCap.speciesIndex)
		if (vPrev === undefined || vCap.timestamp < vPrev) vSpeciesFirstSeen.set(vCap.speciesIndex, vCap.timestamp)
	}

	const vCounts = new Map<string, { captures: number; newSpecies: number }>()
	for (const vCap of inCaptures)
	{
		const vKey = yearMonthKey(vCap.timestamp)
		const vEntry = vCounts.get(vKey) ?? { captures: 0, newSpecies: 0 }
		vEntry.captures += 1
		if (vSpeciesFirstSeen.get(vCap.speciesIndex) === vCap.timestamp) vEntry.newSpecies += 1
		vCounts.set(vKey, vEntry)
	}

	// Build the contiguous month timeline from the earliest bucket to
	// the latest. Then right-align to kMaxMonths.
	const vKeys = [...vCounts.keys()].sort()
	const vFirst = parseYearMonthKey(vKeys[0])
	const vLast = parseYearMonthKey(vKeys[vKeys.length - 1])
	const vBuckets: MonthBucket[] = []
	const vCursor = new Date(Date.UTC(vFirst.year, vFirst.month, 1))
	const vEnd = new Date(Date.UTC(vLast.year, vLast.month, 1))
	while (vCursor.getTime() <= vEnd.getTime())
	{
		const vKey = yearMonthKey(vCursor.getTime())
		const vEntry = vCounts.get(vKey) ?? { captures: 0, newSpecies: 0 }
		const vSeasonIdx = seasonFromTimestamp(vCursor.getTime())  // 0..3 — same enum the captures use
		vBuckets.push({
			yearMonth: vKey,
			captures: vEntry.captures,
			newSpecies: vEntry.newSpecies,
			seasonIndex: vSeasonIdx,
			monthLabel: monthLabel(vCursor.getUTCMonth()),
		})
		vCursor.setUTCMonth(vCursor.getUTCMonth() + 1)
	}

	// Right-align: keep only the last kMaxMonths so a multi-year history
	// doesn't compress bars to invisibility.
	if (vBuckets.length > kMaxMonths) return vBuckets.slice(vBuckets.length - kMaxMonths)
	return vBuckets
}

function yearMonthKey(inTimestamp: number): string
{
	const vDate = new Date(inTimestamp)
	const vYear = vDate.getFullYear()
	const vMonth = String(vDate.getMonth() + 1).padStart(2, '0')
	return `${vYear}-${vMonth}`
}

function parseYearMonthKey(inKey: string): { year: number; month: number }
{
	const [vYearStr, vMonthStr] = inKey.split('-')
	return { year: Number(vYearStr), month: Number(vMonthStr) - 1 }
}

const kMonthLabels: readonly string[] = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

function monthLabel(inMonthIdx: number): string
{
	return kMonthLabels[inMonthIdx] ?? ''
}
